/**
 * Sign in with the corporate Google account, or with a work e-mail and password.
 *
 * The service decides who is allowed (only corporate domains are accepted; password accounts are created
 * by an administrator); this screen only carries the answer. Which doors exist is the service's call too
 * (`GET /v1/ui/login-options`): when a Google client is configured the Google button is the front door
 * and the password form is folded behind a link; otherwise the form is all there is. Every password
 * refusal is the same sentence on purpose — the server does not tell a wrong password from an unknown
 * account, and neither do we. A Google refusal does say why (wrong Workspace), because there is nothing
 * to guess.
 *
 * The dev-only field below is compiled out of production (`import.meta.env.DEV` is a literal `false`
 * there), with its strings inline: a dictionary entry ships to production whatever the tree-shaker
 * makes of the code that used it.
 */

import { Sprout } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent } from 'react'

import { ApiError, type TikTokHubApi } from './api/client'
import type { LoginResponse } from './api/types'
import { renderGoogleButton } from './auth/googleIdentity'
import { useLang, useT } from './i18n'

const HOSTED_DOMAIN = 'mornhouse.com'

export function SignIn({
  api,
  onSignedIn,
  onToken,
}: {
  api: TikTokHubApi
  onSignedIn: (res: LoginResponse) => void
  onToken: (token: string) => void
}) {
  const t = useT()
  const [lang] = useLang()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  //: null until the service has said what it offers; then the Google client id or none
  const [googleClientId, setGoogleClientId] = useState<string | null | undefined>(undefined)
  const [showPassword, setShowPassword] = useState(false)
  const googleSlot = useRef<HTMLDivElement>(null)

  const attempt = async (call: () => Promise<LoginResponse>, refused: (err: ApiError) => string) => {
    setBusy(true)
    setError(null)
    try {
      onSignedIn(await call())
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) setError(t.auth.notConfigured)
      else if (err instanceof ApiError && (err.status === 401 || err.status === 403)) setError(refused(err))
      else setError(t.auth.failed(err instanceof Error ? err.message : String(err)))
    } finally {
      setBusy(false)
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    void attempt(() => api.login(email.trim(), password), () => t.auth.refused)
  }

  useEffect(() => {
    api.loginOptions().then(
      (opts) => setGoogleClientId(opts.google_client_id),
      () => setGoogleClientId(null), // the form still works; the service will say if sign-in is off
    )
  }, [api])

  useEffect(() => {
    const slot = googleSlot.current
    if (!googleClientId || !slot) return
    renderGoogleButton(slot, googleClientId, HOSTED_DOMAIN, lang, (credential) =>
      void attempt(
        () => api.loginWithGoogle(credential),
        (err) => (err.status === 403 ? t.auth.googleWrongDomain : t.auth.googleRefused),
      ),
    ).catch((err: unknown) => setError(t.auth.failed(err instanceof Error ? err.message : String(err))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleClientId, lang])

  const passwordForm = showPassword || googleClientId === null

  return (
    <div className="page" style={{ placeContent: 'center', minHeight: '100vh' }}>
      <form className="card" style={{ maxWidth: 420, margin: '0 auto', width: '100%' }} onSubmit={(e) => void submit(e)}>
        <span className="brand" style={{ fontSize: 19 }}>
          <span className="mark"><Sprout className="ico" size={15} aria-hidden /></span>
          {t.auth.title}
        </span>
        <p className="muted small" style={{ marginTop: 'var(--sp-3)' }}>
          {t.auth.intro}
        </p>
        {googleClientId && (
          <div className="field" style={{ alignItems: 'center' }}>
            <div ref={googleSlot} style={{ minHeight: 44 }} />
            {!showPassword && (
              <button type="button" className="ghost tiny" onClick={() => setShowPassword(true)}>
                {t.auth.usePassword}
              </button>
            )}
          </div>
        )}
        {passwordForm && (
          <>
            <div className="field">
              <label className="field-label" htmlFor="email">
                {t.auth.email}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                placeholder="name@mornhouse.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="password">
                {t.auth.password}
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </>
        )}
        {error && (
          <span className="tiny" style={{ color: 'var(--bad)' }}>
            {error}
          </span>
        )}
        {passwordForm && (
          <div className="row">
            <button className="primary" type="submit" disabled={busy || !email || !password}>
              {busy ? t.auth.signingIn : t.auth.signIn}
            </button>
          </div>
        )}
        <p className="tiny dim" style={{ marginTop: 'var(--sp-3)' }}>
          {t.auth.corporateOnly}
        </p>
        {import.meta.env.DEV && <DevToken onToken={onToken} />}
      </form>
    </div>
  )
}

/** Local development against a service with no users seeded. English-only and inline on purpose. */
function DevToken({ onToken }: { onToken: (token: string) => void }) {
  const [token, setToken] = useState('')
  return (
    <>
      <p className="tiny dim" style={{ marginTop: 'var(--sp-4)' }}>
        Development build: a service API key works here. Production has no such field.
      </p>
      <div className="row">
        <input
          className="grow"
          type="password"
          placeholder="Service token"
          value={token}
          aria-label="Service token"
          onChange={(e) => setToken(e.target.value)}
        />
        <button type="button" disabled={!token.trim()} onClick={() => onToken(token.trim())}>
          Use token
        </button>
      </div>
    </>
  )
}
